import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import FaqJsonLd from "@/components/FaqJsonLd";
import { Button } from "@/components/ui/button";
import { FreeOrgBadge, FREE_ORG_TEXT } from "@/components/FreeOrgBadge";
import { Check, MapPin, Target, Megaphone, Handshake, Rocket, CheckCircle2, ChevronDown,
         Star, Globe, Gift, Users, ShieldCheck, Facebook, Mail, QrCode, Search, Sparkles, FileText } from "lucide-react";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";
import { FounderPriceBlock } from "@/components/FounderPriceBlock";

const slug = (s) => (s || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Tous les avantages apportés au professionnel par PROXIreduc-28
const ADVANTAGES = [
  { icon: FileText,  title: "Demandes de devis gratuites",         text: "Recevez des demandes de devis de particuliers de votre secteur, selon votre métier — réception et réponse 100% gratuites, sans commission ni crédit." },
  { icon: Globe,     title: "Mini-site local référencé Google",  text: "Un vrai mini-site (accueil, produits & services, contact) optimisé SEO, accessible 24h/24." },
  { icon: Search,    title: "Référencement sur les moteurs",      text: "Votre fiche et vos pages sont indexées pour être trouvées par de nouveaux clients proches." },
  { icon: MapPin,    title: "Présence sur la carte locale",       text: "Localisation et communes desservies, visibles par les habitants autour de vous." },
  { icon: Users,     title: "Espace Clients / CRM",               text: "Carnet de contacts, étiquettes et segments pour fidéliser et faire revenir vos clients." },
  { icon: Mail,      title: "Campagnes e-mail avec IA",           text: "Des modèles prêts à l'emploi, avec rédaction et visuels générés par intelligence artificielle." },
  { icon: Sparkles,  title: "Outils IA : rédaction & images",     text: "Rédigez vos offres et créez vos visuels professionnels en quelques secondes." },
  { icon: QrCode,    title: "QR « liste VIP »",                   text: "Collectez des contacts clients en boutique et sur tous vos supports." },
  { icon: Gift,      title: "Offres & promotions illimitées",     text: "Publiez autant d'offres que vous le souhaitez, quand vous le souhaitez." },
  { icon: Facebook,  title: "Diffusion sur les groupes Facebook", text: "Vos offres proposées aux communautés locales PROXIreduc-28 (après validation)." },
  { icon: Megaphone, title: "Mises en avant & blog local",        text: "Nos guides « bons plans » par ville et par métier citent et renvoient vers votre mini-site." },
  { icon: Target,    title: "Statistiques de visibilité",         text: "Suivez en temps réel les vues et les partages de votre mini-site." },
  { icon: Handshake, title: "Réseau local, sans commission",      text: "Rejoignez un réseau qui fait vivre Chartres et l'Eure-et-Loir. Aucune commission sur vos ventes." },
];

const CHANNELS = [
  { icon: Globe,     label: "Mini-site local",     text: "Votre mini-site pro accessible 24/7" },
  { icon: MapPin,    label: "Carte locale",        text: "Présence géographique dans l'Eure-et-Loir" },
  { icon: Gift,      label: "Bons plans & offres", text: "Diffusion de vos promotions" },
  { icon: Megaphone, label: "Communication",       text: "Actualités, mises en avant" },
  { icon: Facebook,  label: "Communautés Facebook locales", text: "Selon formule et conditions de chaque groupe" },
];

const EXAMPLES = [
  { emoji: "🍽️", title: "Restaurant", text: "Dessert offert pour 2 personnes" },
  { emoji: "💇", title: "Coiffeur",   text: "Soin offert avec une prestation" },
  { emoji: "🚗", title: "Garage",     text: "Contrôle visuel offert" },
  { emoji: "🏠", title: "Artisan",    text: "-10 % sur la première intervention" },
  { emoji: "🛍️", title: "Boutique",   text: "10 € offerts dès 50 € d'achat" },
  { emoji: "💆", title: "Institut",   text: "15 minutes de soin offertes" },
];

const CONTROL = [
  "Vous choisissez vos offres",
  "Vous choisissez vos conditions",
  "Vous pouvez modifier vos offres",
  "Vous pouvez créer des offres ponctuelles",
  "Pas de commission sur vos ventes",
  "Vous êtes libre d'arrêter à tout moment",
  "Sans carte bancaire",
  "Sans engagement",
];

const FAQ = [
  { q: "Que comprend la formule Partenaire Fondateur ?",
    a: "Tout : un mini-site local référencé Google / SEO complet, offres et promotions illimitées, mise en avant prioritaire dans les bons plans, l'Espace Clients (CRM + campagnes e-mail avec IA), les outils IA de rédaction et de visuels, des statistiques avancées, et jusqu'à 2 publications Facebook par mois sur les groupes locaux (après validation)." },
  { q: "Combien coûte l'offre Partenaire Fondateur ?",
    a: "0 € pendant 12 mois, réservé aux 50 premiers partenaires activés à Chartres et dans son agglomération. Sans carte bancaire." },
  { q: "Que se passe-t-il à la fin des 12 mois ?",
    a: "Rien d'automatique : aucune facturation et aucune carte bancaire n'est enregistrée. Nous vous recontacterons avant la fin des 12 mois pour vous proposer de continuer, sans aucune obligation." },
  { q: "Dois-je donner ma carte bancaire ?",
    a: "Non. L'activation est gratuite et immédiate, sans carte bancaire." },
  { q: "Suis-je engagé ?",
    a: "Non, c'est sans engagement. Vous restez libre d'arrêter à tout moment depuis votre espace partenaire." },
  { q: "Combien de places sont disponibles ?",
    a: "50 places, réservées aux premiers partenaires de Chartres et de son agglomération." },
  { q: "Puis-je publier plusieurs offres ?",
    a: "Oui. L'offre Partenaire Fondateur permet de publier des offres et promotions en illimité, dans le respect des règles de publication." },
  { q: "Les publications Facebook sont-elles garanties ?",
    a: "Jusqu'à 2 propositions de publication par mois sont prévues sur les groupes Facebook locaux administrés par PROXIreduc-28, après validation. La diffusion dépend des règles des groupes et de la pertinence des contenus : aucun nombre de publications, de vues ou de clients n'est garanti." },
  { q: "Combien de nouveaux clients vais-je obtenir ?",
    a: "PROXIreduc-28 ne garantit aucun nombre de nouveaux clients ni aucun chiffre d'affaires. Nous vous apportons de la visibilité et des outils ; les résultats dépendent de votre activité et de vos offres." },
];

export default function BecomePartner() {
  useSeo({
    title: "Devenir partenaire PROXIreduc-28 — visibilité locale pour pros & commerçants (28)",
    description: "Rejoignez PROXIreduc-28 : mini-site référencé Google, carte de visite digitale, promotions, demandes de devis et CRM. Offre de lancement : 12 mois gratuits, sans carte bancaire, en Eure-et-Loir.",
    canonicalPath: "/devenir-partenaire",
    image: "/og-image.jpg",
  });
  const [cfg, setCfg] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);
  const [content, setContent] = useState({});
  const plansRef = useRef(null);

  useEffect(() => {
    api.get("/partner-config").then((r)=>setCfg(r.data)).catch(()=>setCfg({ plans: [], founders: {}, facebook_groups_count: 17 }));
    api.get("/public/site-content").then((r)=>setContent(r.data||{})).catch(()=>{});
  }, []);

  const navigate = useNavigate();
  const goToSignup = (planKey) => navigate(`/devenir-partenaire/inscription/${planKey || "fondateur"}`);
  const goToPlans = () => plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const foundersRemaining = cfg?.founders?.active ? Math.max(0, cfg.founders.remaining_count || 0) : 0;
  const fbCount = cfg?.facebook_groups_count ?? 17;
  // On n'affiche que l'offre Fondateur (active) et Premium (grisée, formule standard)
  const visiblePlans = (cfg?.plans || []).filter((p) => ["fondateur", "premium"].includes(p.key));

  return (
    <div className="bg-[#FAF8F5]" data-testid="become-partner-page">
      <FaqJsonLd faq={[
        { q: "Comment devenir partenaire PROXIreduc-28 ?", a: "Créez votre compte professionnel gratuitement, activez l'offre Fondateurs et configurez votre mini-site. Vous êtes visible localement en quelques minutes, sans carte bancaire." },
        { q: "Combien coûte l'abonnement professionnel ?", a: "L'offre de lancement est gratuite pendant 12 mois, sans engagement. Aucune commission n'est prélevée sur vos ventes." },
        { q: "Qu'est-ce qui est inclus pour un professionnel ?", a: "Un mini-site référencé Google, une carte de visite digitale, la publication de promotions, la réception de demandes de devis, un CRM, des campagnes e-mail et des outils IA." },
      ]} />
      {/* HERO */}
      <section className="relative overflow-hidden"
        style={content["partner.hero.image"] ? { backgroundImage: `url(${content["partner.hero.image"]})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
        <div className={`absolute inset-0 pointer-events-none ${content["partner.hero.image"] ? "bg-white/75" : "bg-gradient-to-b from-orange-50/60 to-transparent"}`}/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-10 md:pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-orange-200 text-xs font-bold uppercase tracking-widest text-[#E05638]">
              <span>Chartres · Eure-et-Loir</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
              Faites connaître votre activité aux habitants de{" "}
              <span className="text-[#E05638]">Chartres</span> et d'<span className="text-[#E05638]">Eure-et-Loir</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-700" data-testid="partner-hero-subtitle">
              {content["partner.hero.subtitle"] || "Rejoignez PROXIreduc-28, le réseau local qui met en relation les habitants avec les commerces, artisans et professionnels du territoire."}
            </p>
            <div className="mt-5 inline-flex flex-wrap items-center gap-2 rounded-2xl border border-[#E05638]/30 bg-white px-4 py-3 shadow-sm" data-testid="hero-founder-chip">
              <Star size={16} className="text-[#E05638]"/>
              <span className="font-heading font-extrabold text-slate-900">Offre de lancement : <span className="text-[#E05638]">0 € pendant 12 mois</span></span>
              <span className="text-xs text-slate-500">· 50 premières places · sans carte bancaire</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={()=>goToSignup("fondateur")} className="btn-shine text-white h-12 px-6 text-base" data-testid="hero-cta-primary">
                <Rocket size={18} className="mr-2"/> Devenir partenaire fondateur
              </Button>
              <Button variant="outline" onClick={goToPlans} className="h-12 px-6 text-base" data-testid="hero-cta-secondary">
                Voir la formule
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600"/> 0 € pendant 12 mois</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600"/> Sans carte bancaire</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600"/> Sans engagement · aucune commission</span>
            </div>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm" data-testid="partner-seo-badge">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#4285F4]/10">
                <Search size={12} className="text-[#4285F4]"/>
              </span>
              <span className="text-xs font-semibold text-slate-700">Mini-site local <span className="text-[#E05638]">référencé Google / SEO</span></span>
            </div>
            <div className="mt-4 inline-flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
              <FreeOrgBadge size="sm" />
              <span className="text-xs font-semibold text-emerald-900" data-testid="partner-free-org-line">{FREE_ORG_TEXT}</span>
              <Link to="/organismes-collectivites" className="text-xs font-bold text-[#E05638] underline">En savoir plus</Link>
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI REJOINDRE — tous les avantages pro */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16" data-testid="advantages-section">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Pourquoi rejoindre PROXIreduc-28 ?</div>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
            Tous les avantages pour développer votre activité localement
          </h2>
          <p className="mt-2 text-slate-600 text-sm md:text-base">
            Un mini-site, un CRM, des outils IA et un vrai réseau local — tout est inclus dans l'offre Partenaire Fondateur.
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ADVANTAGES.map((r) => (
            <div key={r.title} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3 card-hover" data-testid={`advantage-${slug(r.title)}`}>
              <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center shrink-0"><r.icon size={20}/></div>
              <div>
                <div className="font-heading font-bold text-slate-900">{r.title}</div>
                <p className="mt-1 text-sm text-slate-600">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PLUS QU'UN SITE : UN RÉSEAU LOCAL */}
      <section className="bg-white border-y border-slate-200" data-testid="network-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest">
              Plus qu'un site
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
              PROXIreduc-28 : un vrai <span className="text-[#E05638]">réseau local</span>
            </h2>
            <p className="mt-3 text-slate-700">
              PROXIreduc-28 ne se limite pas à un mini-site local référencé google SEO. Les partenaires peuvent également
              bénéficier d'une visibilité supplémentaire sur notre réseau de communautés Facebook locales, selon
              les possibilités de publication.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            {CHANNELS.map((c, i) => (
              <div key={c.label} className="relative bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col items-start"
                   data-testid={`channel-${i}`}>
                <div className={`w-10 h-10 rounded-lg grid place-items-center ${i === 4 ? "bg-blue-50 text-blue-600" : "bg-white text-[#E05638] border border-slate-200"}`}>
                  <c.icon size={18}/>
                </div>
                <div className="mt-2 font-heading font-bold text-sm text-slate-900">{c.label}</div>
                <p className="text-[11px] text-slate-600 mt-0.5">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS (formule fondateur + premium grisé) */}
      <section ref={plansRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="plans-section">
        <div className="text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Votre formule de lancement</h2>
          <p className="mt-2 text-sm text-slate-600">0 € pendant 12 mois · sans carte bancaire · sans engagement</p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 items-stretch">
          {visiblePlans.map((p) => {
            const greyed = p.disabled || p.key === "premium";
            return (
            <div key={p.key}
                 className={`relative rounded-2xl border p-5 flex flex-col ${greyed ? "bg-slate-50 border-slate-200 opacity-70 saturate-50" : "bg-white border-[#E05638] shadow-lg ring-1 ring-[#E05638]/20"}`}
                 data-testid={`plan-${p.key}`}>
              {p.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest whitespace-nowrap ${greyed ? "bg-slate-400 text-white" : "bg-[#E05638] text-white"}`}>
                  {p.badge}
                </div>
              )}
              <div className="font-heading text-lg font-extrabold text-slate-900">{p.name}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-heading text-3xl font-extrabold text-slate-900">{p.price_display}</span>
                <span className="text-slate-500 text-[11px]">{p.period}</span>
              </div>
              {p.tagline && <p className="mt-1.5 text-xs text-slate-600">{p.tagline}</p>}
              <ul className="mt-3 space-y-1 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-1.5 text-slate-700 text-[12px] leading-tight">
                    <Check size={12} className={`mt-0.5 shrink-0 ${greyed ? "text-slate-400" : "text-emerald-600"}`}/> <span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.facebook_note && (
                <div className="mt-2 text-[10px] italic text-slate-500 border-l-2 border-slate-200 pl-2 leading-tight">{p.facebook_note}</div>
              )}
              {greyed ? (
                <Button disabled className="w-full mt-4 h-auto py-2 px-2 text-sm whitespace-normal leading-tight text-center cursor-not-allowed" variant="outline" data-testid={`plan-cta-${p.key}`}>
                  {p.cta_label || "Bientôt disponible"}
                </Button>
              ) : (
                <Button onClick={()=>goToSignup(p.key)}
                        className="w-full mt-4 min-h-9 h-auto py-2.5 px-2 text-sm whitespace-normal leading-tight text-center btn-shine text-white"
                        data-testid={`plan-cta-${p.key}`}>
                  {p.cta_label || "Choisir"}
                </Button>
              )}
              <Link to="/groupes-facebook" className={`mt-1.5 inline-flex items-center justify-center gap-1 text-[11px] font-semibold ${greyed ? "text-slate-400 pointer-events-none" : "text-blue-700 hover:text-blue-800"}`} data-testid={`plan-fb-link-${p.key}`}>
                <Facebook size={11}/> Nos groupes Facebook →
              </Link>
            </div>
          );})}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">TVA non applicable, art. 293 B du CGI.</p>
      </section>

      {/* CE QUI FAIT LA DIFFÉRENCE (Facebook) */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-testid="difference-section">
        <div className="rounded-3xl bg-gradient-to-br from-blue-50 via-white to-orange-50 border border-blue-200 p-6 md:p-10">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white grid place-items-center shrink-0"><Facebook size={26}/></div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Ce qui fait la différence</div>
              <h2 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
                Votre activité peut également être mise en avant sur notre <span className="text-blue-700">réseau de communautés Facebook locales</span> — plus de {cfg?.fb_members_display || FB_MEMBERS_DISPLAY} membres en cumulé.
              </h2>
              <p className="mt-3 text-slate-700 text-sm md:text-base">
                L'offre Partenaire Fondateur donne accès à des possibilités supplémentaires de communication sur les communautés Facebook locales liées à PROXIreduc-28,
                selon les conditions de chaque groupe et les possibilités de publication.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <ShieldCheck size={14} className="text-emerald-600"/>
                Pas de garantie de nombre de publications, de vues ou de résultats. Une possibilité de visibilité supplémentaire.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RÉSEAU DE COMMUNAUTÉS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-testid="communities-section">
        <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Users size={20}/></div>
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">Un réseau de communautés locales</h2>
          </div>
          <p className="mt-3 text-slate-700 text-sm md:text-base">
            PROXIreduc-28 s'appuie également sur un réseau de <b>{fbCount} communautés Facebook locales</b> réunissant
            <b> plus de {cfg?.fb_members_display || FB_MEMBERS_DISPLAY} membres en cumulé</b>, pour partager les actualités,
            offres et initiatives des professionnels partenaires en Eure-et-Loir.
          </p>
          <Link to="/groupes-facebook" className="mt-4 inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-semibold text-sm" data-testid="communities-cta-link">
            <Facebook size={14}/> Découvrir tous nos groupes Facebook →
          </Link>
        </div>
      </section>

      {/* OFFRE DE LANCEMENT — PARTENAIRE FONDATEUR */}
      {cfg?.founders?.active && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-testid="founders-section">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-[#E05638] text-white p-6 md:p-10">
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none"/>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-widest">
              <Star size={12}/> Offre de lancement — Partenaire Fondateur
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold">Devenez Partenaire Fondateur PROXIreduc-28</h2>
            <FounderPriceBlock variant="dark" className="mt-5 max-w-2xl" />
            <p className="mt-4 text-sm md:text-base font-semibold text-white" data-testid="founders-pitch">
              Pour les 50 premiers partenaires activés à Chartres et dans son agglomération.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 max-w-2xl">
              {[
                "Mini-site local référencé Google / SEO complet",
                "Offres & promotions illimitées",
                "Espace Clients : CRM + campagnes e-mail avec IA",
                "Outils IA de rédaction & génération d'images",
                "Jusqu'à 2 publications Facebook / mois (après validation)",
                "Mise en avant prioritaire + statistiques avancées",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2 text-sm text-white/90"><Check size={16} className="text-emerald-300 shrink-0 mt-0.5"/><span>{t}</span></div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button onClick={()=>goToSignup("fondateur")} className="bg-white text-slate-900 hover:bg-white/90 h-12 px-6 font-extrabold" data-testid="founders-cta">
                <Rocket size={16} className="mr-2"/> Activer ma place fondateur — 0 €
              </Button>
              <span className="text-xs text-white/80 inline-flex items-center gap-1.5">
                <ShieldCheck size={14}/> Sans carte bancaire · sans engagement · activation immédiate
              </span>
            </div>
            <p className="mt-3 text-[11px] text-white/60 max-w-2xl">
              Aucune facturation automatique : rien ne se passe à la fin des 12 mois sans votre accord. Offre réservée aux premiers partenaires, dans la limite des 50 places.
            </p>
          </div>
        </section>
      )}

      {/* VOUS GARDEZ LE CONTRÔLE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 md:p-8" data-testid="control-section">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Votre activité, vos offres, vos règles.</h2>
              <p className="mt-3 text-sm md:text-base text-slate-700">
                Vous gardez le contrôle complet sur votre présence et vos offres. Aucun engagement, aucune carte bancaire.
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-2">
              {CONTROL.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-slate-800">
                  <Check size={16} className="text-emerald-700 mt-0.5 shrink-0"/> {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EXEMPLES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Des offres qui s'adaptent à votre activité</h2>
        <p className="mt-2 text-sm text-slate-600">L'avantage n'est pas forcément une remise. Vous choisissez ce qui correspond le mieux à votre activité.</p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          {EXAMPLES.map((e) => (
            <div key={e.title} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`example-${e.title.toLowerCase()}`}>
              <div className="text-2xl">{e.emoji}</div>
              <div className="mt-1 font-heading font-bold text-slate-900">{e.title}</div>
              <p className="text-xs md:text-sm text-slate-600">« {e.text} »</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 text-center">Questions fréquentes</h2>
        <div className="mt-6 space-y-2">
          {FAQ.map((f, i) => (
            <div key={f.q} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <button onClick={()=>setOpenFaq(openFaq === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-4 text-left"
                      data-testid={`faq-toggle-${i}`}>
                <span className="font-semibold text-slate-900">{f.q}</span>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${openFaq===i?"rotate-180":""}`}/>
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4 text-sm text-slate-700">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold">Votre activité mérite d'être connue localement.</h2>
          <p className="mt-3 text-white/80">Rejoignez les premiers partenaires qui font vivre Chartres et l'Eure-et-Loir.</p>
          <Button onClick={()=>goToSignup("fondateur")} className="mt-6 btn-shine text-white h-12 px-6 text-base" data-testid="final-cta">
            <Rocket size={18} className="mr-2"/> Activer ma place fondateur — 0 €
          </Button>
          <p className="mt-3 text-xs font-semibold text-white/70" data-testid="final-cta-pricing-note">
            <span className="text-emerald-300">0 € pendant 12 mois</span> · sans carte bancaire · sans engagement
          </p>
        </div>
      </section>
    </div>
  );
}
